import React from 'react';

import { listUserProfile, saveUserProfile } from 'stores/UserProfileStoreAdapter';
import { mergeWithDefaults, parseProfileValue } from 'utils/UserProfileGuard';

/**
 * ドメイン Home ダッシュボードの状態を user_profile(AUTO) で保存/復元する共通フック。
 *
 * - マウント時に profileKey の AUTO プロファイルを取得し、defaults にマージして復元する
 *   （未知キーは無視・欠損キーは defaults で補う。RFP の「画面を壊さない」復元ガードに準拠）。
 * - 復元完了後、state 変化のたびにデバウンス（500ms）して保存する（API のため過剰リクエスト抑制）。
 * - 復元・保存の失敗は握りつぶす（UX を止めない）。
 *
 * ページ番号など「データ件数が確定してから補正すべき値」は、呼び出し側で clampPage を通すこと。
 *
 * @param profileKey user_profile の profile_key（例: "contract-home-dashboard"）
 * @param defaults ダッシュボード状態の既定値（形の正）
 * @returns [state, setState, restored] restored は復元完了フラグ
 */
export const useDashboardProfile = <T extends Record<string, unknown>>(
  profileKey: string,
  defaults: T,
): [T, React.Dispatch<React.SetStateAction<T>>, boolean] => {
  const [state, setState] = React.useState<T>(defaults);
  const [restored, setRestored] = React.useState(false);
  // defaults を effect 依存から外すためのラッチ（初回のみ使用）
  const defaultsRef = React.useRef(defaults);

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const results = await listUserProfile(profileKey, 'AUTO');
        if (!active) return;
        if (results.length > 0) {
          const parsed = parseProfileValue<Partial<T>>(results[0].profileValue, {});
          setState(mergeWithDefaults(defaultsRef.current, parsed));
        }
      } catch {
        // 復元失敗は既定値のまま
      } finally {
        if (active) setRestored(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [profileKey]);

  React.useEffect(() => {
    if (!restored) return;
    const timer = setTimeout(() => {
      saveUserProfile({ profileKey, profileValue: JSON.stringify(state) }).catch(() => {
        // 保存失敗は握りつぶす
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [restored, profileKey, state]);

  return [state, setState, restored];
};
